import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '../common/Button';
import { getProjects as getMyProjects } from '../../services/api/timeEntry';
import type { CreateExpenseSheetInput } from '../../services/api/expenses';

interface ExpenseFormProps {
  onSubmit: (input: CreateExpenseSheetInput) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

export default function ExpenseForm({ onSubmit, onCancel, isLoading }: ExpenseFormProps) {
  const [form, setForm] = useState({
    description: '',
    projectId: '',
  });
  const [error, setError] = useState<string | null>(null);

  const { data: projectsRes } = useQuery({
    queryKey: ['myProjects'],
    queryFn: () => getMyProjects(),
  });
  const projects = projectsRes?.data;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!form.projectId) {
      setError('Please select a project.');
      return;
    }

    try {
      await onSubmit({
        description: form.description || undefined,
        projectId: parseInt(form.projectId, 10),
      });
    } catch (err) {
      setError((err as Error).message || 'Failed to create expense sheet.');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
      <h3 className="text-lg font-medium text-gray-900">New Expense</h3>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <input
          type="text"
          value={form.description}
          onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
          placeholder="e.g., Business trip to Prague"
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Project <span className="text-red-500">*</span>
        </label>
        <select
          value={form.projectId}
          onChange={(e) => setForm((prev) => ({ ...prev, projectId: e.target.value }))}
          className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            !form.projectId ? 'border-gray-300' : 'border-gray-300'
          }`}
        >
          <option value="">Select a project...</option>
          {projects
            ?.slice()
            .sort((a, b) => (a.fullName || a.name).localeCompare(b.fullName || b.name))
            .map((p) => (
              <option key={p.id} value={p.id}>{p.fullName || p.name}</option>
            ))}
        </select>
      </div>

      <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
        <Button variant="secondary" type="button" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" isLoading={isLoading}>
          Create
        </Button>
      </div>
    </form>
  );
}
